import axios from "axios";
import i18n from "../../../i18n";

// A duplicate identifier answers 409, which the global error handler turns into
// an unrelated "e-mail taken" alert. Surface the server message instead.
function handleSaveError(context, error) {
  const response = error?.response;
  if (response?.status === 409 && response?.data?.message) {
    context.dispatch("triggerErrorSnackbar", response.data.message);
    return;
  }
  context.dispatch("triggerError", error);
}

const actions = {
  // Vuex always passes context before the payload.
  // eslint-disable-next-line no-unused-vars
  async extractDangerousGoodFromSds(_context, { name, content }) {
    const response = await axios.post("dangerousGood/sds/extract", {
      file: { name, content },
    });
    return response.data.extraction;
  },

  // eslint-disable-next-line no-unused-vars
  async importDangerousGoodFromSds(_context, value) {
    const response = await axios.post("dangerousGood", value);
    return response.data;
  },

  // eslint-disable-next-line no-unused-vars
  async getClientDangerousGoodSummaries(_context) {
    const response = await axios.get(
      "dangerousGood?perPage=100000&select=identifier,name,manufacturer"
    );
    return (response.data.data || []).map((item) => ({
      id: item._id || item.id,
      identifier: item.identifier || "",
      name: item.name || "",
      manufacturer: item.manufacturer || "",
    }));
  },

  // eslint-disable-next-line no-unused-vars
  async getClientDangerousGoodIdentifiers(_context) {
    const response = await axios.get(
      "dangerousGood?perPage=100000&select=identifier"
    );
    return (response.data.data || []).map((item) => ({
      id: item._id || item.id,
      identifier: item.identifier,
    }));
  },

  // eslint-disable-next-line no-unused-vars
  async getDangerousGoodIdentifiers(_context, storageLocationId) {
    const response = await axios.get(
      `dangerousGood?perPage=1000&dangerousGoodStorageLocation=${storageLocationId}`
    );
    return (response.data.data || []).map((item) => item.identifier);
  },

  exportDangerousGoods(context, { storageLocationIds, fields }) {
    return axios
      .post("dangerousGood/export", { storageLocationIds, fields })
      .then(function (r) {
        return r.data;
      })
      .catch(function (e) {
        context.dispatch("triggerError", e);
      });
  },
  approveDangerousGood(context, { id }) {
    return axios
      .post("dangerousGood/" + id + "/approve")
      .then((r) => {
        context.commit(
          "CREATE_SNACKBAR",
          i18n.t("riskAssessment.proof.proofedMessage")
        );
        return r.data;
      })
      .catch((e) => {
        context.dispatch("triggerError", e);
        throw e;
      });
  },

  rejectDangerousGood(context, { id, reason }) {
    return axios
      .post("dangerousGood/" + id + "/reject", { reason })
      .then((r) => {
        context.commit(
          "CREATE_SNACKBAR",
          i18n.t("riskAssessment.proof.rejectedMessage")
        );
        return r.data;
      })
      .catch((e) => {
        context.dispatch("triggerError", e);
        throw e;
      });
  },

  getDangerousGoodVersions(context, id) {
    return axios
      .get("dangerousGood/" + id + "/versions")
      .then((r) => r.data)
      .catch((e) => {
        context.dispatch("triggerError", e);
        return { versions: [], currentVersion: 0, canProof: false };
      });
  },

  createDangerousGoodImport(context, value) {
    return axios
      .post("dangerousGood/batch", value)
      .then(function (r) {
        context.commit(
          "CREATE_SNACKBAR",
          i18n.t("dangerousGoods.dangerousGoodsList.import.resultMessage", {
            success: r.data.successCount,
            failed: r.data.failedCount,
          })
        );
        return r.data;
      })
      .catch(function (e) {
        context.dispatch("triggerError", e);
      });
  },
  createDangerousGood(context, value) {
    return axios
      .post("dangerousGood", value)
      .then(function (r) {
        context.commit(
          "CREATE_SNACKBAR",
          i18n.t("dangerousGoods.dangerousGoodsList.createdMessage", {
            name: r.data.name,
          })
        );
        return r.data;
      })
      .catch(function (e) {
        handleSaveError(context, e);
      });
  },
  deleteDangerousGood(context, val) {
    return axios
      .delete("dangerousGood/" + val.id)
      .then(function (res) {
        context.commit(
          "CREATE_SNACKBAR",
          i18n.t("dangerousGoods.dangerousGoodsList.deletedMessage")
        );
        return res;
      })
      .catch(function (e) {
        context.dispatch("triggerError", e);
      });
  },
  updateDangerousGood(context, value) {
    return axios
      .put("dangerousGood/" + value.id, value)
      .then(function (r) {
        context.commit(
          "CREATE_SNACKBAR",
          i18n.t("dangerousGoods.dangerousGoodsList.updatedMessage", {
            name: r.data.name,
          })
        );
        return r.data;
      })
      .catch(function (e) {
        handleSaveError(context, e);
      });
  },
  getSingleDangerousGood(context, id) {
    return axios
      .get("dangerousGood/" + id)
      .then(function (r) {
        return r.data;
      })
      .catch(function (e) {
        context.dispatch("triggerError", e);
      });
  },

  bulkDeleteDangerousGoods(context, ids) {
    return axios
      .post("dangerousGood/bulk-delete", { ids })
      .then((r) => {
        context.commit(
          "CREATE_SNACKBAR",
          i18n.t("dangerousGoods.dangerousGoodsList.bulk.deletedMessage")
        );
        return r.data;
      })
      .catch((e) => context.dispatch("triggerError", e));
  },

  bulkMoveDangerousGoods(context, { ids, dangerousGoodStorageLocation }) {
    return axios
      .post("dangerousGood/bulk-move", { ids, dangerousGoodStorageLocation })
      .then((r) => {
        context.commit(
          "CREATE_SNACKBAR",
          i18n.t("dangerousGoods.dangerousGoodsList.bulk.movedMessage")
        );
        return r.data;
      })
      .catch((e) => context.dispatch("triggerError", e));
  },

  getAllDangerousGoods(context, { storageLocationId }) {
    return axios
      .get("dangerousGood", {
        params: {
          dangerousGoodStorageLocation: storageLocationId,
        },
      })
      .then(function (r) {
        return r.data;
      })
      .catch(function (e) {
        context.dispatch("triggerError", e);
      });
  },

  moveDangerousGood(context, { id, dangerousGoodStorageLocation }) {
    return axios
      .put(`dangerousGood/${id}`, { dangerousGoodStorageLocation })
      .then(function (r) {
        context.commit(
          "CREATE_SNACKBAR",
          i18n.t("dangerousGoods.dangerousGoodsList.movedMessage", {
            name: r.data.name,
          })
        );
        return r.data;
      })
      .catch((e) => {
        context.dispatch("triggerError", e);
        throw e;
      });
  },
};

export default actions;
